import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Lock } from "lucide-react";
import { Link } from "react-router-dom";

const ease = [0.16, 1, 0.3, 1];
const API = process.env.REACT_APP_BACKEND_URL;

// No Google Ads conversion tracking on this page.
export default function Candidatures() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setStatus("loading");
    try {
      const res = await fetch(`${API}/api/candidatures`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Une erreur est survenue.");
      }
      setStatus("success");
    } catch (err) {
      setStatus("idle");
      setError(err.message || "Une erreur est survenue. Merci de réessayer.");
    }
  };

  return (
    <div data-testid="candidatures-page" className="min-h-screen bg-cream flex flex-col">
      <header className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 h-20 flex items-center justify-between">
          <Link to="/" data-testid="candidatures-logo" className="flex items-center gap-3">
            <img src="/logos/logo-icon-dark.png" alt="DEM BAT" className="h-10 w-auto" />
            <div className="flex items-baseline gap-2">
              <span className="font-serif text-2xl tracking-tight text-charcoal">DEM</span>
              <span className="font-serif text-2xl italic font-light tracking-tight text-bronze">BAT</span>
            </div>
          </Link>
          <Link
            to="/"
            data-testid="candidatures-back-link"
            className="inline-flex items-center gap-2 text-charcoal hover:text-bronze text-xs uppercase tracking-[0.2em] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            Retour au site
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-28 w-full">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease }}
            className="max-w-2xl"
          >
            <div className="inline-flex items-center gap-4 mb-8">
              <span className="h-px w-12 bg-bronze" />
              <span className="text-bronze text-xs uppercase tracking-[0.3em]">Carrières</span>
            </div>

            <h1 className="font-serif text-charcoal text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-normal">
              Candidatures
            </h1>

            <div
              data-testid="candidatures-closed-badge"
              className="mt-10 inline-flex items-center gap-3 bg-charcoal-deep text-white px-5 py-3"
            >
              <Lock className="w-4 h-4" strokeWidth={1.5} />
              <span className="text-xs uppercase tracking-[0.25em]">Équipe complète · Aucun poste ouvert</span>
            </div>

            <p
              data-testid="candidatures-text"
              className="mt-6 font-serif text-charcoal text-2xl sm:text-3xl leading-snug font-normal"
            >
              Nous ne recrutons pas <span className="italic text-bronze">actuellement.</span>
            </p>
            <p className="mt-4 text-charcoal/65 text-base sm:text-lg leading-relaxed font-light">
              Nous conservons toutes les candidatures et vous recontacterons lors de nos
              prochains recrutements.
            </p>

            <div className="mt-12 pt-10 border-t border-charcoal/10">
              {status === "success" ? (
                <div
                  data-testid="candidatures-success"
                  className="flex items-start gap-4 bg-white border border-charcoal/10 p-6"
                >
                  <span className="w-10 h-10 bg-charcoal-deep text-white flex items-center justify-center flex-shrink-0">
                    <Check className="w-5 h-5" strokeWidth={1.5} />
                  </span>
                  <div>
                    <p className="font-serif text-charcoal text-xl">C'est noté.</p>
                    <p className="mt-1 text-charcoal/60 text-sm font-light">
                      Nous vous recontacterons à <span className="text-charcoal">{email}</span> dès l'ouverture d'un poste.
                    </p>
                  </div>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="candidatures-form" noValidate>
                  <label
                    htmlFor="candidature-email"
                    className="block text-charcoal text-sm font-light"
                  >
                    Laissez votre e-mail, nous vous préviendrons en priorité lors de notre prochain recrutement.
                  </label>
                  <div className="mt-4 flex flex-col sm:flex-row gap-3">
                    <input
                      id="candidature-email"
                      type="email"
                      required
                      autoComplete="email"
                      placeholder="votre@email.ch"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid="candidatures-email-input"
                      className="flex-1 bg-white border border-charcoal/20 focus:border-charcoal outline-none px-5 py-4 text-charcoal text-base font-light placeholder:text-charcoal/35 transition-colors"
                    />
                    <button
                      type="submit"
                      disabled={status === "loading"}
                      data-testid="candidatures-submit-button"
                      className="group inline-flex items-center justify-center gap-3 bg-charcoal-deep hover:bg-charcoal disabled:opacity-60 text-white px-8 py-4 text-xs uppercase tracking-[0.25em] transition-colors duration-300"
                    >
                      {status === "loading" ? "Envoi..." : "Me prévenir"}
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
                    </button>
                  </div>
                  {error && (
                    <p data-testid="candidatures-error" className="mt-3 text-red-700 text-sm">
                      {error}
                    </p>
                  )}
                  <p className="mt-4 text-charcoal/45 text-xs font-light">
                    Votre adresse reste confidentielle et ne sert qu'à vous recontacter.
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </main>

      <footer className="border-t border-border py-8">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex flex-col sm:flex-row items-center justify-between gap-3 text-charcoal/40 text-xs">
          <span>© 2026 DEM BAT · Entreprise familiale genevoise</span>
          <span className="uppercase tracking-widest">Design · Excellence · Maîtrise</span>
        </div>
      </footer>
    </div>
  );
}
